/**
 * Utility function to get subnet data based on selected availability zones
 * @param {Object} order - The order object containing availabilityZone and landingZoneDetails
 * @param {Object} options - Configuration options
 * @param {boolean} options.returnSubnetNames - If true, returns subnet names (default: true)
 * @param {boolean} options.returnSubnetIds - If true, returns subnet IDs (default: false)
 * @param {string} options.subnetTier - Filter by subnet tier (default: 'frontend')
 * @returns {Array} Array of subnet names or IDs based on options
 */
export const getSubnets = (order, options = {}) => {
    const { returnSubnetNames = true, returnSubnetIds = false, subnetTier = 'frontend' } = options;

    // Return empty array if no availability zones selected
    if (!order?.availabilityZone || order?.availabilityZone.length === 0) {
        return [];
    }

    const selectedAzs = order.availabilityZone.map((az) => az.value || az);
    const subnets = [];

    // Get all subnets from selected availability zones
    selectedAzs.forEach((azValue) => {
        const azData = order?.landingZoneDetails?.availabilityZones?.find((az) => az.availabilityZone === azValue);

        if (azData?.subnets) {
            const filteredSubnets = azData.subnets.filter((subnet) => subnet.subnetTier === subnetTier);

            filteredSubnets.forEach((subnet) => {
                if (returnSubnetNames && subnet.subnetName) {
                    subnets.push(subnet.subnetName);
                }
                if (returnSubnetIds && subnet.subnetId) {
                    subnets.push(subnet.subnetId);
                }
            });
        }
    });

    return subnets;
};

/**
 * Get subnet names only
 * @param {Object} order - The order object
 * @param {string} subnetTier - Filter by subnet tier (default: 'frontend')
 * @returns {Array} Array of subnet names
 */
export const getSubnetNames = (order, subnetTier = 'frontend') => {
    return getSubnets(order, {
        returnSubnetNames: true,
        returnSubnetIds: false,
        subnetTier,
    });
};

/**
 * Get subnet IDs only
 * @param {Object} order - The order object
 * @param {string} subnetTier - Filter by subnet tier (default: 'frontend')
 * @returns {Array} Array of subnet IDs
 */
export const getSubnetIds = (order, subnetTier = 'frontend') => {
    return getSubnets(order, {
        returnSubnetNames: false,
        returnSubnetIds: true,
        subnetTier,
    });
};

/**
 * Get both subnet names and IDs as objects
 * @param {Object} order - The order object
 * @param {string} subnetTier - Filter by subnet tier (default: 'frontend')
 * @returns {Array} Array of objects with subnetName and subnetId
 */
export const getSubnetDetails = (order, subnetTier = 'frontend') => {
    if (!order?.availabilityZone || order?.availabilityZone.length === 0) {
        return [];
    }

    const selectedAzs = order.availabilityZone.map((az) => az.value || az);
    const subnets = [];

    selectedAzs.forEach((azValue) => {
        const azData = order?.landingZoneDetails?.availabilityZones?.find((az) => az.availabilityZone === azValue);

        if (azData?.subnets) {
            const filteredSubnets = azData.subnets.filter((subnet) => subnet.subnetTier === subnetTier);

            filteredSubnets.forEach((subnet) => {
                subnets.push({
                    subnetName: subnet.subnetName,
                    subnetId: subnet.subnetId,
                    availabilityZone: azValue,
                    subnetTier: subnet.subnetTier,
                });
            });
        }
    });

    return subnets;
};
